import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  Grid, 
  Paper,
  Button,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Divider,
} from '@mui/material';
import { LocationOn, Edit, Delete, Add, Home, Business, Close } from '@mui/icons-material';
import { useUser } from '../../../context/UserContext';
import { apiList, invokeApi } from '../../../services/apiServices';

const AddressBook = () => {
  const { user } = useUser();
  const storageKey = `ar_addresses_${user?.id || user?.email || 'guest'}`;

  const [addresses, setAddresses] = useState([]);
  const [openModal, setOpenModal] = useState(false);
  const [editingAddress, setEditingAddress] = useState(null);
  const [formData, setFormData] = useState({
    type: 'Home',
    name: '',
    address: '',
    city: '',
    state: 'Karnataka',
    pincode: '',
    phone: '',
    isDefault: false,
  });

  useEffect(() => {
    if (!user) return;
    try {
      const saved = JSON.parse(localStorage.getItem(storageKey) || '[]');
      if (Array.isArray(saved) && saved.length > 0) {
        setAddresses(saved);
      } else {
        // Only pre-fill starting address if customer specifically has an address saved on profile
        if (user.address && user.city) {
          const initial = [{
            id: 'addr_1',
            type: 'Home',
            isDefault: true,
            name: user.name || 'Account Holder',
            address: user.address,
            city: user.city,
            state: user.state || 'Karnataka',
            pincode: user.pincode || '',
            phone: user.phone || user.phoneNumber || ''
          }];
          setAddresses(initial);
          localStorage.setItem(storageKey, JSON.stringify(initial));
        } else {
          setAddresses([]);
        }
      }
    } catch (e) {
      setAddresses([]);
    }
  }, [user, storageKey]);

  const saveAddresses = (newAddrs) => {
    setAddresses(newAddrs);
    try {
      localStorage.setItem(storageKey, JSON.stringify(newAddrs));
    } catch (e) {}
  };

  const handleOpenAdd = () => {
    setEditingAddress(null);
    setFormData({
      type: 'Home',
      name: user?.name || user?.fullName || '',
      address: '',
      city: user?.city || '',
      state: user?.state || 'Karnataka',
      pincode: user?.pincode || '',
      phone: user?.phone || user?.phoneNumber || '',
      isDefault: addresses.length === 0,
    });
    setOpenModal(true);
  };

  const handleOpenEdit = (addr) => {
    setEditingAddress(addr);
    setFormData({ ...addr });
    setOpenModal(true);
  };

  const handleDelete = (id) => {
    if (window.confirm('Are you sure you want to delete this address?')) {
      const updated = addresses.filter(a => a.id !== id);
      saveAddresses(updated);
    }
  };

  const handleSetDefault = (id) => {
    const updated = addresses.map(a => ({
      ...a,
      isDefault: a.id === id
    }));
    saveAddresses(updated);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!formData.name.trim() || !formData.address.trim() || !formData.phone.trim()) {
      alert('Please fill in name, address, and contact number.');
      return;
    }

    let updated;
    if (editingAddress) {
      updated = addresses.map(a => a.id === editingAddress.id ? { ...formData, id: a.id } : a);
    } else {
      const newAddr = {
        ...formData,
        id: `addr_${Date.now()}`,
        isDefault: formData.isDefault || addresses.length === 0
      };
      if (newAddr.isDefault) {
        updated = addresses.map(a => ({ ...a, isDefault: false }));
        updated.push(newAddr);
      } else {
        updated = [...addresses, newAddr];
      }
    }
    saveAddresses(updated);
    setOpenModal(false);
  };

  return (
    <Box>
      <Box sx={{ mb: 4, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <LocationOn sx={{ color: '#0E6ADA', fontSize: 28 }} />
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 800, color: '#1A237E' }}>
              Saved Addresses
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Manage billing and delivery addresses for {user?.name || user?.email}
            </Typography>
          </Box>
        </Box>
        <Button 
          variant="contained" 
          startIcon={<Add />}
          onClick={handleOpenAdd}
          sx={{
            bgcolor: '#0E6ADA',
            color: '#FFF',
            fontWeight: 700,
            borderRadius: 2,
            textTransform: 'none',
            px: 2.5,
            boxShadow: '0 4px 12px rgba(14,106,218,0.25)',
            '&:hover': { bgcolor: '#0b54b0' }
          }}
        >
          Add New Address
        </Button>
      </Box>

      {addresses.length > 0 ? (
        <Grid container spacing={3}>
          {addresses.map((addr) => (
            <Grid item xs={12} sm={6} key={addr.id}>
              <Paper 
                variant="outlined" 
                sx={{ 
                  p: 3, 
                  borderRadius: 2.5, 
                  border: addr.isDefault ? '2px solid #0E6ADA' : '1px solid #E2E8F0',
                  position: 'relative',
                  transition: 'all 0.25s',
                  bgcolor: addr.isDefault ? '#F8FAFC' : '#FFF',
                  '&:hover': { borderColor: '#0E6ADA', boxShadow: '0 4px 16px rgba(0,0,0,0.06)' }
                }}
              >
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {addr.type === 'Office' ? <Business fontSize="small" sx={{ color: '#0E6ADA' }} /> : <Home fontSize="small" sx={{ color: '#0E6ADA' }} />}
                    <Typography variant="subtitle2" sx={{ fontWeight: 800, color: '#1E293B' }}>{addr.type}</Typography>
                    {addr.isDefault && (
                      <Chip label="Default" size="small" sx={{ bgcolor: '#0E6ADA', color: '#fff', fontSize: '11px', fontWeight: 700, height: '20px' }} />
                    )}
                  </Box>
                  <Box>
                    <IconButton size="small" onClick={() => handleOpenEdit(addr)} title="Edit">
                      <Edit fontSize="small" />
                    </IconButton>
                    <IconButton size="small" color="error" onClick={() => handleDelete(addr.id)} title="Delete">
                      <Delete fontSize="small" />
                    </IconButton>
                  </Box>
                </Box>
                
                <Typography variant="body1" sx={{ fontWeight: 700, color: '#0F172A', mb: 0.5 }}>{addr.name}</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5, lineHeight: 1.5 }}>{addr.address}</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>{addr.city}, {addr.state || 'Karnataka'} - {addr.pincode}</Typography>
                <Typography variant="body2" sx={{ mt: 1, fontWeight: 600, color: '#334155' }}>Phone: {addr.phone}</Typography>

                {!addr.isDefault && (
                  <Button
                    size="small"
                    onClick={() => handleSetDefault(addr.id)}
                    sx={{ mt: 2, textTransform: 'none', color: '#0E6ADA', fontWeight: 600, p: 0 }}
                  >
                    Set as Default
                  </Button>
                )}
              </Paper>
            </Grid>
          ))}
        </Grid>
      ) : (
        <Paper
          elevation={0}
          sx={{
            textAlign: 'center',
            py: 8,
            px: 3,
            border: '1.5px dashed #CBD5E1',
            borderRadius: 3,
            bgcolor: '#F8FAFC'
          }}
        >
          <LocationOn sx={{ fontSize: 50, color: '#94A3B8', mb: 1.5 }} />
          <Typography variant="h6" sx={{ fontWeight: 700, color: '#1E293B', mb: 0.5 }}>
            No Saved Addresses
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 450, mx: 'auto', mb: 3 }}>
            Add your primary billing and delivery address for faster one-click checkout on license purchases.
          </Typography>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={handleOpenAdd}
            sx={{
              bgcolor: '#0E6ADA',
              color: '#FFF',
              fontWeight: 700,
              borderRadius: 2,
              px: 3,
              py: 1.2,
              textTransform: 'none',
              boxShadow: '0 4px 14px rgba(14,106,218,0.3)',
              '&:hover': { bgcolor: '#0b54b0' }
            }}
          >
            Add Address
          </Button>
        </Paper>
      )}

      {/* ── Address Form Modal ── */}
      <Dialog
        open={openModal}
        onClose={() => setOpenModal(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1 }}>
          <Typography variant="h6" sx={{ fontWeight: 800 }}>
            {editingAddress ? 'Edit Address' : 'Add New Address'}
          </Typography>
          <IconButton size="small" onClick={() => setOpenModal(false)}>
            <Close fontSize="small" />
          </IconButton>
        </DialogTitle>
        <Divider />
        <Box component="form" onSubmit={handleSubmit}>
          <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2.5 }}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Contact Name"
                  required
                  size="small"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Phone Number"
                  required
                  size="small"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Street Address / Office Unit"
                  required
                  multiline
                  rows={2}
                  size="small"
                  value={formData.address}
                  onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  label="City"
                  required
                  size="small"
                  value={formData.city}
                  onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  label="State"
                  required
                  size="small"
                  value={formData.state}
                  onChange={(e) => setFormData({ ...formData, state: e.target.value })}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  label="PIN Code"
                  required
                  size="small"
                  value={formData.pincode}
                  onChange={(e) => setFormData({ ...formData, pincode: e.target.value })}
                />
              </Grid>
            </Grid>
          </DialogContent>
          <Divider />
          <DialogActions sx={{ p: 2 }}>
            <Button onClick={() => setOpenModal(false)} sx={{ textTransform: 'none', color: '#64748B' }}>
              Cancel
            </Button>
            <Button 
              type="submit" 
              variant="contained" 
              sx={{ bgcolor: '#0E6ADA', color: '#FFF', fontWeight: 700, borderRadius: 2, textTransform: 'none', px: 3, '&:hover': { bgcolor: '#0b54b0' } }}
            >
              {editingAddress ? 'Save Changes' : 'Add Address'}
            </Button>
          </DialogActions>
        </Box>
      </Dialog>
    </Box>
  );
};

export default AddressBook;
